import { useCallback, useRef, useState, useEffect } from 'react';
import { Crop, PercentCrop } from 'react-image-crop';
import { $document, $window } from '@umu/umu-dom';
import { isSafari } from '@umu/umu-ua';
import html2canvas from 'html2canvas';
import domToImage from 'dom-to-image-more';
import { checkIsAuthCDNUrl, setImgCrossOrigin } from '@umu/umu-util';
import { reportError } from 'common/src/services/errorLogger';
import * as ImageUtil from '@umu/umu-image-handle';
import { dealCheckVal } from '../uploadImageAndCrop/util';
import { SOURCE_IMG_TYPE } from '../uploadImageAndCrop/interface';
import { EMediaType } from '@umu/umu-types';
import { getUploadResult } from '../../services';
import { UploadLoading } from '../@uploadLoading/uploadLoading';
import {
    checkIsOriginalImage,
    checkLegacyOriginalImage,
    setupCanvasContext,
    calculateCropParams,
    drawImageWithTransform,
    calculateNewTransform,
} from './utils';
import { IProps, ImageTransform, OPERATE_TYPE } from './interface';

const pixelRatioDefault = $window?.devicePixelRatio || 1;

export const useImgCropPortal = (props: IProps) => {
    const {
        src,
        teacherId,
        cropRatio = 16 / 9,
        multiple = 1,
        isShowToolBar = false,
        uploadLoadText,
        onCancel,
        onConfirm,
        onErr,
        onChangeImg,
    } = props;

    // ==================== 状态管理 ====================
    const imgRef = useRef<HTMLImageElement>();
    const canvasRef = useRef<HTMLCanvasElement>();
    const [disabled, setDisabled] = useState(true);
    const [imgUrl, setImgUrl] = useState(src || '');
    const [isSession, setIsSession] = useState(false);
    const [completedCrop, setCompletedCrop] = useState<Crop | null>(null);
    const [crop, setCrop] = useState<Crop>({ unit: '%', width: 100, aspect: cropRatio });
    const [imageTransform, setImageTransform] = useState<ImageTransform>({
        rotation: 0,
        scaleX: 1,
        scaleY: 1,
        translateX: 0,
        translateY: 0,
    });
    const [isOriginalImage, setIsOriginalImage] = useState(false);

    // ==================== 图片加载处理 ====================
    const onLoadErr = useCallback(() => {
        const newUrl = ImageUtil.appendWidth(imgUrl, 256);
        imgUrl !== newUrl && setImgUrl(newUrl);
    }, [imgUrl]);

    const onLoad = useCallback(
        (img: any) => {
            imgRef.current = img;
            const canvas = $document.createElement('canvas');
            canvasRef.current = canvas;
            setDisabled(false);
            const isOriginal = checkIsOriginalImage(img.width, img.height, props.cropRatio);
            setIsOriginalImage(isOriginal);
        },
        [props.cropRatio],
    );

    // ==================== 会话图片处理 ====================
    const getSessionImg = async (ele: HTMLElement) => {
        if (isSafari) {
            const canvas = await html2canvas(ele, {
                useCORS: true,
                allowTaint: true,
                backgroundColor: null,
            });
            return canvas.toDataURL('image/png');
        } else {
            const domToImageOptions: Record<string, boolean> = {};
            if (checkIsAuthCDNUrl(ele?.outerHTML)) {
                domToImageOptions.useCredentials = true;
            }
            return await domToImage.toPng(ele);
        }
    };

    const init = async () => {
        const elementId = 'imgSessionImg';
        const ele = $document.getElementById(elementId);

        if (ele) {
            ele.style.display = 'table-cell';
            ele.style.width = `${4 * cropRatio}rem`;
            ele.style.height = '4rem';
            try {
                const newSrc = await getSessionImg(ele);
                ele.style.display = 'none';
                setImgUrl(newSrc);
                setIsSession(false);
            } catch (error) {
                reportError({
                    error: error as Error,
                    componentName: 'ImgCropPortal',
                    additionalInfo: { desc: 'init error during session image capture', elementId },
                });
            }
        }
    };

    useEffect(() => {
        if (props.sourceImgType === SOURCE_IMG_TYPE.SESSION) {
            setIsSession(true);
            init();
        }
    }, [props.sourceImgType]);

    // ==================== 裁剪事件处理 ====================
    const onCropChange = useCallback((newCrop: Crop, _percentCrop: PercentCrop): void => {
        setCrop(newCrop);
    }, []);

    const onCropComplete = useCallback((newCrop: Crop, _percentCrop: PercentCrop): void => {
        setCompletedCrop(newCrop);
    }, []);

    // ==================== 上传处理逻辑 ====================
    const blobBack = useCallback(
        (blob: Blob | null): void => {
            if (!blob) {
                UploadLoading.hide();
                onErr();
                return;
            }

            const previewUrl = $window.URL.createObjectURL(blob);
            const param = {
                blob,
                ext: 'png',
                previewUrl,
                fileName: '',
                mediaType: EMediaType.IMAGE,
                teacherId,
            };
            getUploadResult(param)
                .then((payloadData) => {
                    setDisabled(false);
                    UploadLoading.hide();
                    $window.URL.revokeObjectURL(previewUrl);
                    onConfirm(payloadData.url);
                    onCancel();
                })
                .catch(() => {
                    setDisabled(false);
                    UploadLoading.hide();
                    $window.URL.revokeObjectURL(previewUrl);
                    onErr();
                });
        },
        [teacherId, onConfirm, onCancel, onErr],
    );

    // ==================== 原图处理逻辑 ====================
    const shouldUseOriginalImage = useCallback((): boolean => {
        if (!isShowToolBar && isOriginalImage) {
            const image = imgRef.current;
            if (!image) {
                return false;
            }
            return checkIsOriginalImage(image.width, image.height, props.cropRatio);
        }
        return false;
    }, [isShowToolBar, isOriginalImage, props.cropRatio]);

    const handleOriginalImage = useCallback((): void => {
        if (!/blob/.test(imgUrl) && dealCheckVal(imgUrl)) {
            onConfirm(imgUrl);
            onCancel();
        } else if (/blob/.test(imgUrl)) {
            fetch(imgUrl).then(async (res) => {
                blobBack(await res.blob());
            });
        }
    }, [imgUrl, onConfirm, onCancel, blobBack]);

    // ==================== 裁剪处理逻辑 ====================
    const performImageCrop = useCallback(
        (image: HTMLImageElement, canvas: HTMLCanvasElement, completedCrop: Crop) => {
            setDisabled(true);
            const basePixelRatio = pixelRatioDefault * multiple;
            // pixelRatio 应该是设备像素比，不应该乘以图片缩放比例
            const pixelRatio = basePixelRatio;

            UploadLoading.show({ message: uploadLoadText });

            const params = calculateCropParams(image, completedCrop, props.cropRatio, imageTransform);

            canvas.width = params.completedCropWidth * pixelRatio;
            canvas.height = params.completedCropHeight * pixelRatio;

            const ctx = setupCanvasContext(canvas, pixelRatio);
            const img = document.createElement('img');

            let url = imgUrl;
            if (!/blob/.test(imgUrl) && dealCheckVal(imgUrl) && props.sourceImgType !== SOURCE_IMG_TYPE.SESSION) {
                url = `${imgUrl}${/\?/.test(imgUrl) ? '&' : '?'}t=${Date.now()}`;
            }

            img.src = url;
            setImgCrossOrigin(img, url);

            img.onload = () => {
                setTimeout(() => {
                    // 使用原始的 image 对象，而不是新创建的 img 对象
                    // 因为原始的 image 对象有正确的显示尺寸
                    drawImageWithTransform(ctx, image, params, canvas, imageTransform, pixelRatio);
                    canvas.toBlob(blobBack, 'image/png', 1);
                }, 1000);
            };

            img.onerror = () => {
                setDisabled(false);
                UploadLoading.hide();
                onErr();
            };
        },
        [imageTransform, multiple, uploadLoadText, props.cropRatio, imgUrl, props.sourceImgType, blobBack, onErr],
    );

    const confirmHandle = useCallback((): void => {
        const image = imgRef.current!;
        const canvas = canvasRef.current!;
        const completedCropWidth = completedCrop?.width || 200;

        if (!completedCrop || disabled) {
            return;
        }

        if (shouldUseOriginalImage()) {
            handleOriginalImage();
            return;
        }

        if (checkLegacyOriginalImage(image, completedCropWidth, props.cropRatio)) {
            handleOriginalImage();
            return;
        }

        performImageCrop(image, canvas, completedCrop);
    }, [completedCrop, disabled, shouldUseOriginalImage, handleOriginalImage, props.cropRatio, performImageCrop]);

    // ==================== 工具栏操作逻辑 ====================
    const applyTransformToDOM = useCallback((transform: ImageTransform): void => {
        if (imgRef.current) {
            const transformStyle = `rotate(${transform.rotation}deg) scale(${transform.scaleX}, ${transform.scaleY})`;
            imgRef.current.style.transform = transformStyle;
        }
    }, []);

    const onImageToolBar = useCallback(
        (type: OPERATE_TYPE): void => {
            if (disabled) {
                return;
            }

            const typeMap = {
                [OPERATE_TYPE.ROTATE]: 'ROTATE' as const,
                [OPERATE_TYPE.ZOOM_IN]: 'ZOOM_IN' as const,
                [OPERATE_TYPE.ZOOM_OUT]: 'ZOOM_OUT' as const,
            };
            const newTransform = calculateNewTransform(typeMap[type], imageTransform);
            setImageTransform(newTransform);
            applyTransformToDOM(newTransform);
            setIsOriginalImage(false);
        },
        [disabled, imageTransform, applyTransformToDOM],
    );

    // ==================== 返回值 ====================
    return {
        // 状态
        imgUrl,
        isSession,
        crop,

        // 事件处理
        onLoadErr,
        onLoad,
        onCropChange,
        onCropComplete,
        confirmHandle,
        onImageToolBar,
        onChangeImg,
        onCancel,
    };
};
